/**
 * IndexedDB Storage Service
 * Handles offline storage, encryption keys, and sync queue
 */

import Dexie, { type Table } from 'dexie';

// ============================================================================
// Database Schema
// ============================================================================

interface EncryptionKey {
  id: string; // user_id
  key: CryptoKey;
  createdAt: Date;
  updatedAt: Date;
}

interface SyncQueueItem {
  id?: number;
  type: 'ritual' | 'journal' | 'habit' | 'completion';
  action: 'create' | 'update' | 'delete';
  data: Record<string, unknown>;
  userId: string;
  createdAt: Date;
  retries: number;
  lastError?: string;
}

interface JournalDraft {
  id: string;
  content: string;
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
}

interface UserSecret {
  id: string; // userId + key name
  userId: string;
  key: string;
  value: string;
  createdAt: Date;
  updatedAt: Date;
}

interface Recording {
  id: string;
  filename: string;
  blob: Blob;
  duration: number;
  createdAt: Date;
}

class SovereignMindDB extends Dexie {
  encryptionKeys!: Table<EncryptionKey, string>;
  syncQueue!: Table<SyncQueueItem, number>;
  journalDrafts!: Table<JournalDraft, string>;
  userSecrets!: Table<UserSecret, string>;
  recordings!: Table<Recording, string>;

  constructor() {
    super('SovereignMindDB');
    this.version(3).stores({
      encryptionKeys: 'id',
      syncQueue: '++id, type, userId, createdAt',
      journalDrafts: 'id, createdAt, updatedAt',
      userSecrets: 'id, userId, key',
      recordings: 'id, filename, createdAt',
    });
  }
}

const db = new SovereignMindDB();

// ============================================================================
// Encryption Key Management
// ============================================================================

/**
 * Store encryption key for user
 */
export async function storeEncryptionKey(userId: string, key: CryptoKey): Promise<void> {
  try {
    await db.encryptionKeys.put({
      id: userId,
      key,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error('Failed to store encryption key:', error);
    throw error;
  }
}

/**
 * Get encryption key for user
 */
export async function getEncryptionKey(userId: string): Promise<CryptoKey | null> {
  try {
    const record = await db.encryptionKeys.get(userId);
    return record?.key || null;
  } catch (error) {
    console.error('Failed to get encryption key:', error);
    return null;
  }
}

/**
 * Delete encryption key for user (on logout)
 */
export async function deleteEncryptionKey(userId: string): Promise<void> {
  try {
    await db.encryptionKeys.delete(userId);
  } catch (error) {
    console.error('Failed to delete encryption key:', error);
  }
}

// ============================================================================
// Sync Queue Management
// ============================================================================

/**
 * Add item to sync queue
 */
export async function addToSyncQueue(
  type: SyncQueueItem['type'],
  action: SyncQueueItem['action'],
  data: Record<string, unknown>,
  userId: string
): Promise<number> {
  try {
    const id = await db.syncQueue.add({
      type,
      action,
      data,
      userId,
      createdAt: new Date(),
      retries: 0,
    });
    return id as number;
  } catch (error) {
    console.error('Failed to add to sync queue:', error);
    throw error;
  }
}

/**
 * Get sync queue items
 */
export async function getSyncQueueItems(userId: string): Promise<SyncQueueItem[]> {
  try {
    return await db.syncQueue.where('userId').equals(userId).toArray();
  } catch (error) {
    console.error('Failed to get sync queue items:', error);
    return [];
  }
}

/**
 * Remove item from sync queue
 */
export async function removeFromSyncQueue(id: number): Promise<void> {
  try {
    await db.syncQueue.delete(id);
  } catch (error) {
    console.error('Failed to remove from sync queue:', error);
  }
}

/**
 * Update sync queue item (for retries)
 */
export async function updateSyncQueueItem(
  id: number,
  updates: Partial<SyncQueueItem>
): Promise<void> {
  try {
    await db.syncQueue.update(id, updates);
  } catch (error) {
    console.error('Failed to update sync queue item:', error);
  }
}

/**
 * Clear sync queue for user
 */
export async function clearSyncQueue(userId: string): Promise<void> {
  try {
    await db.syncQueue.where('userId').equals(userId).delete();
  } catch (error) {
    console.error('Failed to clear sync queue:', error);
  }
}

// ============================================================================
// Journal Draft Management
// ============================================================================

/**
 * Save journal draft
 */
export async function saveJournalDraft(
  id: string,
  content: string,
  tags: string[] = []
): Promise<void> {
  try {
    await db.journalDrafts.put({
      id,
      content,
      tags,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error('Failed to save journal draft:', error);
  }
}

/**
 * Get journal draft
 */
export async function getJournalDraft(id: string): Promise<JournalDraft | null> {
  try {
    return (await db.journalDrafts.get(id)) || null;
  } catch (error) {
    console.error('Failed to get journal draft:', error);
    return null;
  }
}

/**
 * Delete journal draft
 */
export async function deleteJournalDraft(id: string): Promise<void> {
  try {
    await db.journalDrafts.delete(id);
  } catch (error) {
    console.error('Failed to delete journal draft:', error);
  }
}

/**
 * Get all journal drafts
 */
export async function getAllJournalDrafts(): Promise<JournalDraft[]> {
  try {
    return await db.journalDrafts.orderBy('updatedAt').reverse().toArray();
  } catch (error) {
    console.error('Failed to get journal drafts:', error);
    return [];
  }
}

/**
 * Save recording to IndexedDB
 */
export async function saveRecording(filename: string, blob: Blob, duration: number = 0): Promise<void> {
  try {
    await db.recordings.put({
      id: filename,
      filename,
      blob,
      duration,
      createdAt: new Date(),
    });
  } catch (error) {
    console.error('Failed to save recording:', error);
    throw error;
  }
}

/**
 * Get recording from IndexedDB
 */
export async function getRecording(filename: string): Promise<Recording | null> {
  try {
    return (await db.recordings.get(filename)) || null;
  } catch (error) {
    console.error('Failed to get recording:', error);
    return null;
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if IndexedDB is available
 */
export function isIndexedDBAvailable(): boolean {
  return typeof window !== 'undefined' && 'indexedDB' in window;
}

// ============================================================================
// User Secrets Management
// ============================================================================

/**
 * Save user secret (e.g., encryption salt)
 */
export async function saveUserSecret(
  userId: string,
  key: string,
  value: string
): Promise<void> {
  try {
    const id = `${userId}:${key}`;
    await db.userSecrets.put({
      id,
      userId,
      key,
      value,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error('Failed to save user secret:', error);
    throw error;
  }
}

/**
 * Get user secret
 */
export async function getUserSecret(
  userId: string,
  key: string
): Promise<string | null> {
  try {
    const id = `${userId}:${key}`;
    const record = await db.userSecrets.get(id);
    return record?.value || null;
  } catch (error) {
    console.error('Failed to get user secret:', error);
    return null;
  }
}

/**
 * Delete user secret
 */
export async function deleteUserSecret(
  userId: string,
  key: string
): Promise<void> {
  try {
    const id = `${userId}:${key}`;
    await db.userSecrets.delete(id);
  } catch (error) {
    console.error('Failed to delete user secret:', error);
  }
}

/**
 * Clear all user secrets for a user
 */
export async function clearUserSecrets(userId: string): Promise<void> {
  try {
    await db.userSecrets.where('userId').equals(userId).delete();
  } catch (error) {
    console.error('Failed to clear user secrets:', error);
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Clear all data (for logout or reset)
 */
export async function clearAllData(): Promise<void> {
  try {
    await db.encryptionKeys.clear();
    await db.syncQueue.clear();
    await db.journalDrafts.clear();
    await db.userSecrets.clear();
  } catch (error) {
    console.error('Failed to clear all data:', error);
  }
}

